import { useState } from 'react';
import moment from 'moment';
import { ChevronDown, CornerDownRight, ListFilter, ListTodo } from 'lucide-react';
import { HIDDEN_BY_DEFAULT, TASK_STATUSES } from './_enums';
import StatusBadge from './components/StatusBadge';
import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Error } from '@/components/ui/error';
import { Loading } from '@/components/ui/loading';
import { Empty, EmptyIcon, EmptyTitle, EmptyDescription } from '@/components/ui/empty';
import { Table, TableBody, TableHead, TableHeader, TableRow, TableCell } from '@/components/ui/table';
import { cn } from '@/helpers/shadcn';

import { useZealTasks } from '@/data/zealTask';

// Read-only, in Zeal's order: the API lists each parent followed by its subtasks.
const TaskList = () => {
  // Track what is hidden rather than what is shown, so a status this page does not know yet still appears.
  const [hidden, setHidden] = useState(HIDDEN_BY_DEFAULT);
  const tasks = useZealTasks();

  const handleToggleStatus = (status, checked) => {
    setHidden((current) => (checked ? current.filter((s) => s !== status) : [...current, status]));
  };

  const renderBody = () => {
    if (tasks.isLoading && !tasks.data) {
      return <Loading />;
    }
    if (tasks.error) {
      return <Error message={`An error occurred loading tasks: ${tasks.error.message}`} />;
    }

    const visible = tasks.data.query.map((id) => tasks.data.content[id]).filter((task) => !hidden.includes(task.status));

    if (visible.length === 0) {
      return (
        <Empty className="min-h-[280px] rounded-lg border border-dashed">
          <EmptyIcon>
            <ListTodo />
          </EmptyIcon>
          <EmptyTitle>No Tasks Found</EmptyTitle>
          <EmptyDescription>Try showing more statuses</EmptyDescription>
        </Empty>
      );
    }

    return (
      <div className="space-y-3">
        <div className="text-sm text-muted-foreground">
          Showing <span className="font-medium text-foreground">{visible.length}</span> of {tasks.data.query.length} tasks
        </div>
        <div className="overflow-hidden rounded-lg border">
          <Table>
            <TableHeader className="bg-muted/50">
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-[90px]">ID</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Assignee</TableHead>
                <TableHead>Created By</TableHead>
                <TableHead className="text-right">Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((task) => (
                <TableRow key={task.id} className={cn(task.parent && 'bg-muted/20')}>
                  <TableCell className="whitespace-nowrap py-3 font-mono text-xs text-muted-foreground">{task.id}</TableCell>
                  <TableCell className="py-3">
                    <div className={cn('flex items-start gap-2', task.parent && 'pl-5')}>
                      {task.parent && <CornerDownRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-label={`Subtask of ${task.parent}`} />}
                      <span className={cn(!task.parent && 'font-medium')}>{task.title}</span>
                    </div>
                  </TableCell>
                  <TableCell className="py-3 text-muted-foreground">{task.type}</TableCell>
                  <TableCell className="py-3">
                    <StatusBadge status={task.status} />
                  </TableCell>
                  <TableCell className="py-3">{task.assignee || <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="py-3">{task.createdByName || task.createdBy || <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="whitespace-nowrap py-3 text-right text-muted-foreground">{task.createdAt ? moment(task.createdAt).format('MMM D, YYYY') : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="space-y-1.5">
          <CardTitle className="text-xl">All tasks</CardTitle>
          <CardDescription>Every task in Zeal, read-only. Status is the one Zeal shows.</CardDescription>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">
              <ListFilter />
              Status
              <span className="rounded-full bg-muted px-1.5 text-xs tabular-nums">
                {TASK_STATUSES.filter((status) => !hidden.includes(status)).length}/{TASK_STATUSES.length}
              </span>
              <ChevronDown className="opacity-60" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel>Show statuses</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {TASK_STATUSES.map((status) => (
              <DropdownMenuCheckboxItem
                key={status}
                checked={!hidden.includes(status)}
                onCheckedChange={(checked) => handleToggleStatus(status, checked)}
                onSelect={(e) => e.preventDefault()}
              >
                <StatusBadge status={status} />
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </CardHeader>
      <CardContent>{renderBody()}</CardContent>
    </Card>
  );
};

export default TaskList;
